import {
  ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import {
  GetCampaignByIdNoTagsGQL, GetTagGroups2GQL, TagGroupInfoFragment, TagType, GetTagGroups2
} from 'generated/graphql';

import {
  filterGroupCategoriesByArchiveStatus,
  filterGroupCategoriesByTagType,
  filterGroupsByText, filterReadonlyCategories, getFieldTypeIcon
} from 'app/field-shared/field-helper-functions';
import { AccountRoleService } from 'auth/account-role.service';
import { WithChangesSubject } from 'core/mixins/with-changes-subject';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { MatSelectionListChange } from '@angular/material/list';

import { FeatureFlagService } from 'feature-flags/feature-flag.service';

@Component({
  selector: 'app-field-search-inline',
  templateUrl: './field-search-inline.component.html',
})
export class FieldSearchInlineComponent extends WithDisposableSubject(WithChangesSubject(class { })) implements OnChanges, OnInit {
  loading = true;

  groups: TagGroupInfoFragment[] = [];

  get visibleCategoryCount() {
    return this.groups.flatMap(c => c.categories).length;
  }

  /**
   * Emits event of visible groups after all filter functions have run.
   * Can be used for select all functionality implemented outside this component
   */
  @Output() groupsChange = new EventEmitter<TagGroupInfoFragment[]>();

  /**
   * Readonly tag categories will be excluded except
   * for leads and admins
   */
  @Input() hideReadonly: boolean;

  /**
   * Hides universal tag categories
   */
  @Input() hideUniversal: boolean;

  /**
   * Hide select all checkbox
   */
  @Input() hideSelectAll: boolean;

  /**
   * Limit fields to only those for EntityType or EntityConnectionType
   * also required if targetTypeId is provided.
   */
  @Input() targetType: 'EntityType' | 'EntityConnectionType' | 'CalendarEventType';

  /**
   * Limit fields to only these tag types
   */
  @Input() tagTypes: Array<TagType>;

  /**
   * Limit fields to only EntityTypes or EntityConnectionTypes
   * for the following id
   */
  @Input() targetTypeId: string;

  /** Output when targetTypeId is changed */
  @Output() targetTypeIdChange = new EventEmitter<string>();

  /**
   *  Only display entities when in the following campiagn
   *  Required if withTypeSelect is true
   */
  @Input() campaignId: string;

  /**
   * Includes inline filter dropdown to set
   * for a specific entity type or entity connection type.
   * A campaignId is required.
   */
  @Input() withTypeSelect: boolean;

  /**
   * Enables multiselect checkbox
   */
  @Input() multiple: boolean;

  /** Used in conjuction with multiselect checkbox */
  @Input() selectedOptions: { id: string }[];

  /** Optional function to filter visible results */
  @Input() filterOptionsFn: (groups: TagGroupInfoFragment[]) => TagGroupInfoFragment[];

  /**
   * Emits when field is selected
   */
  @Output() itemSelected = new EventEmitter<any>();

  /**
   * When multiselect is enabled, emits the selectedCategories
   */
  @Output() selectedOptionsChange = new EventEmitter<{ id: string }[]>();

  filterTextControl = new UntypedFormControl();

  /** View model options for entity type */
  entityTypeOptions: { id: string; namePlural: string }[];
  entityConnectionTypeOptions: any[];

  /**
   * Selected entity type for the entity type select
   * if enabled
   */
  selectedEntityType: { id: string; namePlural: string };

  /** If target type is for connections */
  selectedEntityConnectionType: any;

  /** Helper */
  getFieldTypeClass = getFieldTypeIcon;

  constructor(
    private _cd: ChangeDetectorRef,
    private _featureFlagService: FeatureFlagService,
    private _getTagGroupsGQL: GetTagGroups2GQL,
    private _getCampaignByIdNoTags: GetCampaignByIdNoTagsGQL,
    private _accountRoleService: AccountRoleService
  ) {
    super();
  }

  ngOnInit() {
    if (this.withTypeSelect && this.campaignId) {
      this._getCampaignByIdNoTags.fetch({ campaignId: this.campaignId })
        .subscribe(({ data }) => {
          this.entityTypeOptions = data.getCampaign.entityTypes;
          this.entityConnectionTypeOptions = data.getCampaign.entityConnectionTypes;
          this._cd.markForCheck();
        });
    }
    this._initFiltering();
    this._watchOnChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
  }

  onItemSelected(event: MatSelectionListChange) {
    const categoryValue = event.options[0].value;
    this.itemSelected.emit(categoryValue);
    if (!categoryValue) {
      return;
    }

    if (!this.multiple) {
      this._cd.markForCheck();
      return;
    }

    if (event.options[0].selected) {
      if (this.isSelected(event.options[0].value)) {
        return;
      } else {
        this.selectedOptionsChange.emit([...this.selectedOptions, categoryValue]);
      }
    } else {
      this.selectedOptionsChange.emit(
        (this.selectedOptions || []).filter(o => o.id !== categoryValue?.id)
      );
    }
  }

  selectAllClicked() {
    const visibleCats = this.groups.flatMap(c => c.categories);
    // Newly selected categories
    const newCats = visibleCats
      .filter(vc => !this.selectedOptions
        .map(so => so.id)
        .includes(vc.id)
    );

    this.selectedOptionsChange.emit([...this.selectedOptions, ...newCats]);
  }

  isSelected(categoryId: any) {
    if (this.multiple) {
      return this.selectedOptions && this.selectedOptions.map(o => o.id).includes(categoryId);
    }
  }

  entityTypeSelected(selectedType: { id: string; namePlural: string }) {
    this.targetTypeId = selectedType?.id;
    this.selectedEntityType = selectedType;
    this.targetTypeIdChange.emit(selectedType?.id);
    this.fetchTagGroups();
  }

  entityConnectionTypeSelected(selectedType: any) {
    this.targetTypeId = selectedType?.id;
    this.selectedEntityConnectionType = selectedType;
    this.targetTypeIdChange.emit(selectedType?.id);
    this.fetchTagGroups();
  }

  private _watchOnChanges() {
    this.watchForChangeKeys('targetType', 'targetTypeId', 'campaignId').pipe(
      takeUntil(this.disposer)
    ).subscribe((c) => {
      this.fetchTagGroups();
    });
  }

  private fetchTagGroups() {
    this.filterTextControl.setValue(
      this.filterTextControl.value
    );
  }

  private _initFiltering() {
    this.filterTextControl.valueChanges.pipe(
      startWith(''),
      switchMap(searchText => this._getTagGroupsGQL.fetch({
        targetType: this.targetType,
        targetTypeId: this.targetTypeId,
        campaignId: this.campaignId
      }).pipe(
        map(res => res.data.getTagGroups.nodes),
        map(groups => {
          if (this.hideReadonly) {
            return filterReadonlyCategories(this._accountRoleService.userRole, groups);
          }
          return groups;
        }),
        map(groups => {
          if (this.hideUniversal) {
            console.log('filtering out universal');
            return groups.filter(g => g.isUniversal === false);
          }
          return groups;
        }),
        map(groups => filterGroupCategoriesByArchiveStatus(groups, 'Active')),
        map(groups => filterGroupCategoriesByTagType(groups, this.tagTypes)),
        map(groups => {
          if (this.filterOptionsFn) {
            return this.filterOptionsFn(groups);
          }
          return groups;
        }),
        map(groups => filterGroupsByText(searchText, groups))
      ))
    ).subscribe((groups => {
      this.groups = groups;
      this.groupsChange.emit(groups);
      this.loading = false;
      this._cd.markForCheck();
    }));
  }
}
